import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, Button, Stack, Alert, Divider } from '@mui/material';
import { useIntl } from 'react-intl';
import { PhoneIphone, ContentCopy, CheckCircle, OpenInBrowser } from '@mui/icons-material';
import { isInAppBrowser } from './ActivationPage';
import Safari from '/public/assets/images/safari.jpeg';
import Chrome from '/public/assets/images/chrome.jpeg';
import Browsers from '/public/assets/images/browsers.png';

/**
 * Detekuje typ prohlížeče
 */
const detectBrowser = (): 'safari' | 'chrome' | 'other' => {
  const ua = navigator.userAgent;

  // Safari
  if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
    return 'safari';
  }

  // Chrome
  if (ua.indexOf('Chrome') > -1) {
    return 'chrome';
  }

  return 'other';
};

export const BrowserGuidePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const intl = useIntl();

  const token = searchParams.get('token');
  const [copied, setCopied] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [browserType, setBrowserType] = useState<'safari' | 'chrome' | 'other'>('other');

  useEffect(() => {
    setIsInApp(isInAppBrowser());
    setBrowserType(detectBrowser());
  }, []);

  const fullUrl = token
    ? `${window.location.origin}/customer-reservations?token=${token}`
    : `${window.location.origin}/customer-reservations`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleContinue = () => {
    if (token) {
      navigate(`/customer-reservations?token=${token}`, { replace: true });
    } else {
      navigate('/customer-reservations', { replace: true });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stack spacing={3} alignItems="center">
          <PhoneIphone sx={{ fontSize: 60, color: 'primary.main' }} />

          <Typography variant="h5" gutterBottom textAlign="center">
            {intl.formatMessage({
              id: 'browserGuide.title',
              defaultMessage: 'Vítejte v rezervačním systému',
            })}
          </Typography>

          {/* In-App Browser Warning */}
          {isInApp && (
            <>
              <img
                src={Browsers}
                style={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                }}
              />

              <Alert severity="warning" sx={{ width: '100%' }}>
                <Typography variant="body2" gutterBottom>
                  {intl.formatMessage({
                    id: 'browserGuide.inAppWarning',
                    defaultMessage:
                      'Zjistili jsme, že jste odkaz otevřeli v aplikaci (Messenger, Instagram, apod.). Pro správné fungování rezervačního systému prosím otevřete odkaz v Safari nebo Chrome.',
                  })}
                </Typography>
              </Alert>

              <Divider sx={{ width: '100%' }} />

              <Typography variant="h6" textAlign="center">
                {intl.formatMessage({
                  id: 'browserGuide.howToOpen',
                  defaultMessage: 'Jak otevřít v prohlížeči?',
                })}
              </Typography>

              <Stack spacing={2} sx={{ width: '100%' }}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    {intl.formatMessage({
                      id: 'browserGuide.step1',
                      defaultMessage: '1. Zkopírujte odkaz:',
                    })}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                    onClick={handleCopyLink}
                    fullWidth
                    color={copied ? 'success' : 'primary'}
                  >
                    {copied
                      ? intl.formatMessage({
                          id: 'browserGuide.copied',
                          defaultMessage: 'Zkopírováno!',
                        })
                      : intl.formatMessage({
                          id: 'browserGuide.copyLink',
                          defaultMessage: 'Zkopírovat odkaz',
                        })}
                  </Button>
                </Paper>

                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2">
                    {intl.formatMessage({
                      id: 'browserGuide.step2',
                      defaultMessage:
                        '2. \n  a) Otevřete Safari nebo Chrome a vložte odkaz do adresního řádku, \n  b) Kliknětě vpravo nahoře na tři tečky (⋮) a "Otevřít v Safari/Chrome".',
                    })}
                  </Typography>
                </Paper>
              </Stack>

              <Alert severity="info" sx={{ width: '100%' }}>
                {intl.formatMessage({
                  id: 'browserGuide.whyBrowser',
                  defaultMessage:
                    'Safari a Chrome si pamatují vaše přihlášení, takže se nebudete muset přihlašovat při každé návštěvě.',
                })}
              </Alert>
            </>
          )}

          {/* Regular Browser Info */}
          {!isInApp && (
            <>
              <img
                src={
                  browserType === 'safari' ? Safari : browserType === 'chrome' ? Chrome : Browsers
                }
                alt={
                  browserType === 'safari'
                    ? 'Safari'
                    : browserType === 'chrome'
                      ? 'Chrome'
                      : 'Prohlížeče'
                }
                style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 8 }}
              />
              <Alert
                severity={browserType === 'safari' || browserType === 'chrome' ? 'success' : 'info'}
                sx={{ width: '100%' }}
              >
                <Typography variant="body2">
                  {browserType === 'safari'
                    ? intl.formatMessage({
                        id: 'browserGuide.safariDetected',
                        defaultMessage:
                          'Skvělé! Používáte Safari. Tento prohlížeč si bude pamatovat vaše přihlášení.',
                      })
                    : browserType === 'chrome'
                      ? intl.formatMessage({
                          id: 'browserGuide.chromeDetected',
                          defaultMessage:
                            'Skvělé! Používáte Chrome. Tento prohlížeč si bude pamatovat vaše přihlášení.',
                        })
                      : intl.formatMessage({
                          id: 'browserGuide.otherBrowser',
                          defaultMessage:
                            'Pro opakované použití je nutné otevřít odkaz v Safari nebo Chrome, které si pamatují vaše přihlášení.',
                        })}
                </Typography>
              </Alert>

              <Divider sx={{ width: '100%' }} />

              <Stack spacing={2} sx={{ width: '100%' }}>
                <Paper sx={{ p: 2, bgcolor: 'primary.light' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CheckCircle color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {intl.formatMessage({
                          id: 'browserGuide.benefit1',
                          defaultMessage: 'Zůstanete přihlášeni až 90 dní',
                        })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({
                          id: 'browserGuide.benefit1Detail',
                          defaultMessage: 'Nemusíte se přihlašovat při každé návštěvě',
                        })}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Paper sx={{ p: 2, bgcolor: 'primary.light' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CheckCircle color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {intl.formatMessage({
                          id: 'browserGuide.benefit2',
                          defaultMessage: 'Rychlá a snadná rezervace',
                        })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({
                          id: 'browserGuide.benefit2Detail',
                          defaultMessage: 'Rezervujte termín kdykoliv během pár kliknutí',
                        })}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>

              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<OpenInBrowser />}
                onClick={handleContinue}
              >
                {intl.formatMessage({
                  id: 'browserGuide.continue',
                  defaultMessage: 'Pokračovat do rezervačního systému',
                })}
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};
